/**
 * Fan-in / synthesis state for a parent session's parallel subagent fan-out.
 *
 * The operator problem this solves: during a fan-out every child renders its
 * own RUNNING / Cancel / Retry badge, so "how far along is the whole thing"
 * and "can synthesis start yet" are both unanswerable. The operator's only
 * recourse is to type "I think you are stuck" into the parent chat.
 *
 * Everything here is pure and derived from the card set plus an explicit
 * synthesis record. No timers, no I/O — the renderer calls `deriveFanIn` on
 * every paint, so the states below advance automatically as child statuses
 * change (`chat.run` / lifecycle events already mutate the cards) without the
 * operator having to prompt anything.
 *
 * Vocabulary:
 *   - "child"  = one parallel subagent session spawned by the parent.
 *   - "handoff"= a child's result delivered back to the parent transcript.
 *   - "settled"= a child that can no longer change (done / failed / cancelled).
 *   - "outstanding" = a child that must settle before full synthesis is safe.
 */
import type { ChatSubagentCard } from "../ui-types.ts";
import {
  isSubagentRunCancellable,
  resolveSubagentActivityPhase,
} from "./subagent-run-status.ts";

/**
 * Fan-in phase. Ordered by "how close synthesis is to being possible":
 * `idle` < `running` < `waiting-handoffs` < `ready` / `partial` <
 * `synthesizing` < `synthesized` (or `synthesis-failed`).
 */
export type FanInPhase =
  /** No children — nothing to aggregate. */
  | "idle"
  /** At least one child is active; synthesis is not yet on the table. */
  | "running"
  /**
   * Some children have handed off, others are still outstanding. This is the
   * state the recording never signalled: the operator could see two green
   * children and three spinners but no statement that synthesis is blocked.
   */
  | "waiting-handoffs"
  /** Every child settled successfully and no synthesis has run yet. */
  | "ready"
  /**
   * Every child settled, but at least one failed / was cancelled. Synthesis
   * is still possible — just on a reduced result set, which is why this is a
   * distinct state from `ready` rather than a sub-case.
   */
  | "partial"
  /** Synthesis was requested and is in flight. */
  | "synthesizing"
  /** Synthesis landed in the parent transcript. */
  | "synthesized"
  /** Synthesis was requested but did not land (error, or timed out). */
  | "synthesis-failed";

/** Lifecycle of the synthesis attempt itself, as tracked by the UI host. */
export type FanInSynthesisState = "none" | "requested" | "running" | "done" | "failed";

export type FanInSynthesisRecord = {
  state: FanInSynthesisState;
  /** Epoch ms of the request, used for "requested 2m ago" copy. */
  requestedAt?: number;
  /** Short human-readable reason, e.g. transport error text. */
  detail?: string;
};

export type FanInSnapshot = {
  phase: FanInPhase;
  /** Every child in the fan-out. */
  total: number;
  /** Children that can no longer change. */
  settled: number;
  /** Settled cleanly with a usable result. */
  completed: number;
  /** Failed or defunct. */
  failed: number;
  /** Operator-stopped. */
  cancelled: number;
  /** Still running / queued / blocked — synthesis depends on these. */
  outstanding: number;
  /** Paused on an operator question; not making progress. */
  blocked: number;
  /**
   * Children synthesis is waiting on, in creation order. Surfaced so the UI
   * can name them ("waiting on: latency-audit, edge-cases") instead of only
   * counting them.
   */
  blocking: ChatSubagentCard[];
  /** "3/5 audits complete" — the progress half of the headline. */
  progressLabel: string;
  /** "synthesis waiting on 2 handoffs" — the dependency half. */
  dependencyLabel: string;
  /** Single-line operator summary combining the two. */
  headline: string;
  /** Plain-language state name for the status chip. */
  stateLabel: string;
  /** Full synthesis is safe: every child settled with a usable result. */
  canSynthesize: boolean;
  /** Reduced synthesis is safe: at least one result, some children failed. */
  canForceSynthesize: boolean;
  /** At least one child is still cancellable. */
  canCancelAll: boolean;
  synthesis: FanInSynthesisRecord;
};

/** Singular/plural helper — "1 task" / "3 tasks". */
function plural(n: number, singular: string, pluralForm = `${singular}s`): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/**
 * Nominal task label for the progress counter. Reads better than a bare
 * fraction when the fan-out is audits; falls back to "tasks" otherwise.
 *
 * Scans label *and* task, because in practice the label is a topic
 * ("latency") while the task carries the intent ("audit latency"). Matching
 * on the label alone reported "3/5 tasks" for what the operator was calling a
 * set of audits.
 */
export function fanInNoun(cards: ChatSubagentCard[]): string {
  const auditish = cards.filter((c) => {
    const haystack = `${c.label ?? ""} ${c.task ?? ""}`.toLowerCase();
    return haystack.includes("audit") || haystack.includes("review pass");
  }).length;
  return auditish > 0 && auditish >= Math.ceil(cards.length / 2) ? "audits" : "tasks";
}

/**
 * Per-child bucketing used by both the snapshot and the tests. Kept
 * separate so the Inspector legend and the fan-in bar cannot drift.
 */
export function classifyFanInChildren(cards: ChatSubagentCard[]): {
  completed: ChatSubagentCard[];
  failed: ChatSubagentCard[];
  cancelled: ChatSubagentCard[];
  outstanding: ChatSubagentCard[];
  blocked: ChatSubagentCard[];
} {
  const completed: ChatSubagentCard[] = [];
  const failed: ChatSubagentCard[] = [];
  const cancelled: ChatSubagentCard[] = [];
  const outstanding: ChatSubagentCard[] = [];
  const blocked: ChatSubagentCard[] = [];
  for (const card of cards) {
    const phase = resolveSubagentActivityPhase(card);
    if (phase === "completed") {
      completed.push(card);
    } else if (phase === "failed" || phase === "defunct") {
      failed.push(card);
    } else if (phase === "cancelled") {
      cancelled.push(card);
    } else if (phase === "blocked") {
      // Blocked is outstanding (synthesis still waits) but is called out
      // separately: it is the child most likely wedged, and it is the one
      // case where "waiting on N handoffs" should read as a problem.
      blocked.push(card);
      outstanding.push(card);
    } else {
      outstanding.push(card);
    }
  }
  return { completed, failed, cancelled, outstanding, blocked };
}

/**
 * Derive the full fan-in picture for a set of children.
 *
 * `synthesis` is the host-tracked synthesis record. When omitted the state
 * machine still works — it just cannot report `synthesizing` /
 * `synthesized` / `synthesis-failed`, because those require knowing that a
 * request was made.
 */
export function deriveFanIn(
  cards: ChatSubagentCard[],
  synthesis: FanInSynthesisRecord = { state: "none" },
): FanInSnapshot {
  const total = cards.length;
  const parts = classifyFanInChildren(cards);
  const completed = parts.completed.length;
  const failed = parts.failed.length;
  const cancelled = parts.cancelled.length;
  const blocked = parts.blocked.length;
  const outstanding = parts.outstanding.length;
  const settled = completed + failed + cancelled;

  const noun = fanInNoun(cards);
  const progressLabel = total === 0 ? "no subagents" : `${completed}/${total} ${noun} complete`;

  // Phase: synthesis state wins once a request exists, otherwise derive from
  // the child buckets. Ordering matters — a synthesis that failed while new
  // children spawned still reports the failure, because that is the action
  // the operator needs to take next.
  let phase: FanInPhase;
  if (total === 0) {
    phase = "idle";
  } else if (synthesis.state === "done") {
    phase = "synthesized";
  } else if (synthesis.state === "failed") {
    phase = "synthesis-failed";
  } else if (synthesis.state === "requested" || synthesis.state === "running") {
    phase = "synthesizing";
  } else if (outstanding > 0) {
    // Distinguish "nothing has landed yet" from "some landed, some pending":
    // the latter is the state the operator could not previously see.
    phase = completed + failed + cancelled > 0 ? "waiting-handoffs" : "running";
  } else if (failed > 0 || cancelled > 0) {
    phase = "partial";
  } else {
    phase = "ready";
  }

  // Dependency label: name the children synthesis is blocked on.
  const blocking = [...parts.outstanding].sort((a, b) => a.createdAt - b.createdAt);
  let dependencyLabel: string;
  if (phase === "idle") {
    dependencyLabel = "";
  } else if (blocking.length > 0) {
    const names = blocking
      .slice(0, 3)
      .map((c) => c.label?.trim() || "unnamed")
      .join(", ");
    const more = blocking.length > 3 ? ` +${blocking.length - 3} more` : "";
    dependencyLabel = `synthesis waiting on ${plural(blocking.length, "handoff")} — ${names}${more}`;
  } else {
    dependencyLabel = "all handoffs in";
  }

  const stateLabel = fanInStateLabel(phase);

  // Action availability is derived from child health, not from `phase`:
  // `phase` folds in the synthesis record, so a failed synthesis over a
  // healthy fan-out still has to offer the operator a full retry. Deciding
  // from health keeps "is it safe?" independent of "did it run?".
  // `total > 0` guards vacuous truth: with no children every bucket is zero, so
  // `allSettledCleanly` would be vacuously true and the bar would offer to
  // synthesize nothing.
  const allSettledCleanly = total > 0 && outstanding === 0 && failed === 0 && cancelled === 0;
  const canSynthesize = allSettledCleanly && synthesis.state !== "done";
  // Forcible when at least one usable result exists and something was lost —
  // or is still pending. Not offered when synthesis is already the primary
  // action, so the operator is never shown two ways to do the same thing.
  const canForceSynthesize =
    !canSynthesize && completed > 0 && (failed > 0 || cancelled > 0 || outstanding > 0);
  const canCancelAll =
    cards.some((c) => isSubagentRunCancellable(c.status)) && synthesis.state !== "done";

  const headline =
    phase === "idle"
      ? "No parallel subagents for this session"
      : `${progressLabel} · ${dependencyLabel}`;

  return {
    phase,
    total,
    settled,
    completed,
    failed,
    cancelled,
    outstanding,
    blocked,
    blocking,
    progressLabel,
    dependencyLabel,
    headline,
    stateLabel,
    canSynthesize,
    canForceSynthesize,
    canCancelAll,
    synthesis,
  };
}

/** Chip copy for each phase. Plain language, no wire vocabulary. */
export function fanInStateLabel(phase: FanInPhase): string {
  switch (phase) {
    case "idle":
      return "No fan-out";
    case "running":
      return "Subagents running";
    case "waiting-handoffs":
      return "Waiting for handoffs";
    case "ready":
      return "Ready to synthesize";
    case "partial":
      return "Partial results available";
    case "synthesizing":
      return "Synthesizing…";
    case "synthesized":
      return "Synthesis complete";
    case "synthesis-failed":
      return "Synthesis failed";
    default:
      return String(phase);
  }
}

/** CSS modifier for the chip, matching the existing badge convention. */
export function fanInPhaseBadgeClass(phase: FanInPhase): string {
  switch (phase) {
    case "ready":
    case "synthesized":
      return "done";
    case "partial":
      return "warn";
    case "synthesis-failed":
      return "error";
    case "waiting-handoffs":
    case "synthesizing":
      return "running";
    case "running":
      return "running";
    case "idle":
    default:
      return "idle";
  }
}

/**
 * True when the fan-in still needs operator attention. Drives the
 * attention badge so a blocked fan-out is visible without opening the tab.
 */
export function fanInNeedsAttention(snapshot: FanInSnapshot): boolean {
  if (snapshot.phase === "synthesis-failed") return true;
  if (snapshot.phase === "ready") return true;
  if (snapshot.blocked > 0) return true;
  return false;
}

/** 0–1 completion ratio, for the progress meter. */
export function fanInProgressRatio(snapshot: FanInSnapshot): number {
  if (snapshot.total === 0) return 0;
  return snapshot.settled / snapshot.total;
}

/**
 * Text for the primary action button. Distinguishes the two synthesis modes
 * so the operator knows whether they are getting the full set or a reduced
 * one.
 */
export function fanInSynthesizeActionLabel(snapshot: FanInSnapshot): string {
  if (snapshot.canSynthesize) return "Synthesize";
  if (snapshot.phase === "partial") {
    return `Force synthesis (${snapshot.completed} of ${snapshot.total} results)`;
  }
  return "Force synthesis with available results";
}
